// The router's error page. Two jobs: (1) a lazy chunk that vanished after a
// deploy (the tab predates the new build) reloads the page ONCE, silently,
// so the owner lands on the fresh build; (2) everything else — and a second
// chunk failure — gets an Arabic page with a reload and a way home, instead
// of react-router's English «Unexpected Application Error!».
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { BrandMark } from '@/components/BrandMark';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunk-reload';

export const ROUTE_ERROR_COPY = {
  staleTitle: 'صدرت نسخة جديدة من التطبيق',
  staleBody: 'تعذّر تحميل هذه الصفحة لأن نسخة أحدث نُشرت. أعد التحميل للمتابعة من حيث كنت.',
  notFoundTitle: 'الصفحة غير موجودة',
  notFoundBody: 'الرابط الذي فتحته لا يقود إلى صفحة في احجزلي ERP.',
  title: 'حدث خطأ غير متوقع',
  body: 'تعذّر عرض هذه الصفحة. أعد التحميل، أو ارجع إلى الرئيسية.',
  reload: 'إعادة التحميل',
  home: 'الرئيسية',
  loading: 'جارٍ تحديث التطبيق',
} as const;

export function RouteError({ homePath = '/' }: { homePath?: string }) {
  const error = useRouteError();
  const stale = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!stale) return;
    if (reloadOnceForChunkError()) setReloading(true);
  }, [stale]);

  useEffect(() => {
    if (!stale) console.error('route error', error);
  }, [stale, error]);

  if (reloading) {
    return (
      <div className="e-center" data-testid="route-error-reloading">
        <BrandMark mode="loading" size={72} />
        <span className="sr-only">{ROUTE_ERROR_COPY.loading}</span>
      </div>
    );
  }

  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const title = stale ? ROUTE_ERROR_COPY.staleTitle : notFound ? ROUTE_ERROR_COPY.notFoundTitle : ROUTE_ERROR_COPY.title;
  const body = stale ? ROUTE_ERROR_COPY.staleBody : notFound ? ROUTE_ERROR_COPY.notFoundBody : ROUTE_ERROR_COPY.body;

  return (
    <div className="e-center">
      <section className="e-errcard" data-kind={stale ? 'stale-build' : notFound ? 'not-found' : 'crash'} role="alert">
        <span className="e-errcard-icon" aria-hidden="true">
          <BrandMark mode="static" size={28} tone="mist" />
        </span>
        <div className="e-errcard-head">
          <h2 className="e-errcard-title">{title}</h2>
        </div>
        <p className="e-errcard-body">{body}</p>
        <div className="e-errcard-actions">
          <button type="button" className="btn primary" onClick={() => window.location.reload()}>
            {ROUTE_ERROR_COPY.reload}
          </button>
          <a className="btn ghost" href={homePath}>
            {ROUTE_ERROR_COPY.home}
          </a>
        </div>
      </section>
    </div>
  );
}
